export type EntityId = number;
export type PacketId = number;

export interface GridPos {
  x: number;
  y: number;
}

export interface GridRect {
  min: GridPos;
  max: GridPos;
}

export type Direction = 'north' | 'east' | 'south' | 'west';

export const DIRECTIONS: readonly Direction[] = ['north', 'east', 'south', 'west'];

const OFFSETS: Record<Direction, GridPos> = {
  north: { x: 0, y: -1 },
  east: { x: 1, y: 0 },
  south: { x: 0, y: 1 },
  west: { x: -1, y: 0 },
};

export function samePos(a: GridPos, b: GridPos): boolean {
  return a.x === b.x && a.y === b.y;
}

export function translate(pos: GridPos, direction: Direction): GridPos {
  const offset = OFFSETS[direction];
  return { x: pos.x + offset.x, y: pos.y + offset.y };
}

const OPPOSITE: Record<Direction, Direction> = {
  north: 'south',
  south: 'north',
  east: 'west',
  west: 'east',
};

export function oppositeDirection(direction: Direction): Direction {
  return OPPOSITE[direction];
}

const RADIANS: Record<Direction, number> = {
  east: 0,
  south: Math.PI / 2,
  west: Math.PI,
  north: -Math.PI / 2,
};

export function directionToRadians(direction: Direction): number {
  return RADIANS[direction];
}

function columnLabel(x: number): string {
  let label = '';
  let n = x;
  while (n >= 0) {
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
  }
  return label;
}

export function toExcel(pos: GridPos): string {
  return `${columnLabel(pos.x)}${pos.y + 1}`;
}

function parseColumnLabel(label: string): number {
  let x = 0;
  for (let i = 0; i < label.length; i++) {
    x = x * 26 + (label.charCodeAt(i) - 64);
  }
  return x - 1;
}

export function fromExcel(s: string): GridPos {
  const match = /^([A-Z]+)(\d+)$/.exec(s);
  if (!match) throw new Error(`Invalid Excel coordinate: "${s}"`);
  const x = parseColumnLabel(match[1]);
  const y = Number(match[2]) - 1;
  if (y < 0) throw new Error(`Invalid Excel coordinate: "${s}"`);
  return { x, y };
}
